export { HeaderNavigation } from './component'
import {
  ICtaButtonMvp as IContentfulCtaButton,
  ICtaLinkMvp as IContentfulCtaLink,
  ISignupCta as IContentfulSignupCta,
  IVariationContainer as IContentfulVariationContainer,
} from 'marketing-site/@types/generated/contentful'
import { IProps as ICTALinkMVP } from 'marketing-site/src/mvp/library/elements/CTALink/'
import { IProps as IMutinyCtasContainer } from 'marketing-site/src/library/components/MutinyCtasContainer/'
import { IProps as ISubNav } from 'marketing-site/src/mvp/library/components/SubNav'
import { Icon } from 'marketing-site/src/mvp/library/utils/icons'
import { IProps as IImage } from 'marketing-site/src/library/elements/Image'

export const getUrlWithPageviewParam = (address: string) => {
  /*
   * TEMP_URL represents the base URL to use in cases where address is a
   * relative URL (this is required by the URL constructor). Before returning,
   * we remove TEMP_URL using the replace() string method.
   */
  const TEMP_URL = 'https://www.relative.temp'

  const url = new URL(address, TEMP_URL)
  url.searchParams.append('on_pageview_event', 'nav')

  return url.toString().replace(TEMP_URL, '')
}

export const isHeaderNavigationLink = (
  item: IHeaderNavigationMenu | IHeaderNavigationLink,
): item is IHeaderNavigationLink => {
  return item && !!(item as IHeaderNavigationLink).url
}

export const isHeaderNavigationMenu = (
  item: IHeaderNavigationMenu | IHeaderNavigationLink,
): item is IHeaderNavigationMenu => {
  return item && !!(item as IHeaderNavigationMenu).content
}

export const isHeaderNavigationMenuDescription = (
  item:
    | IHeaderNavigationMenuDescription
    | IHeaderNavigationSpotlight
    | IHeaderNavigationMenuSection
    | IHeaderNavigationSolutions,
): item is IHeaderNavigationMenuDescription => {
  return item && !!(item as IHeaderNavigationMenuDescription).contentType
}

export const isHeaderNavigationMenuSection = (
  item:
    | IHeaderNavigationMenuDescription
    | IHeaderNavigationSpotlight
    | IHeaderNavigationMenuSection
    | IHeaderNavigationSolutions,
): item is IHeaderNavigationMenuSection => {
  return item && !!(item as IHeaderNavigationMenuSection).items
}

export const isHeaderNavigationSection = (
  item:
    | IHeaderNavigationMenuSection
    | IHeaderNavigationMenuDescription
    | IHeaderNavigationSolutions,
): item is IHeaderNavigationMenuSection => {
  return item && !!(item as IHeaderNavigationMenuSection).items
}

export const isHeaderNavigationSolutions = (
  item:
    | IHeaderNavigationMenuDescription
    | IHeaderNavigationSpotlight
    | IHeaderNavigationMenuSection
    | IHeaderNavigationSolutions,
): item is IHeaderNavigationSolutions => {
  return item && !!(item as IHeaderNavigationSolutions).solutions
}

export const isHeaderNavigationSpotlight = (
  item:
    | IHeaderNavigationMenuDescription
    | IHeaderNavigationSpotlight
    | IHeaderNavigationMenuSection
    | IHeaderNavigationSolutions,
): item is IHeaderNavigationSpotlight => {
  return item && !!(item as IHeaderNavigationSpotlight).media
}

export interface IHeaderNavigationDesktop {
  contentType?: 'flex' | 'grid'
  theme?: 'light' | 'dark' | 'resolution-bot'
  navigationElements: (IHeaderNavigationMenu | IHeaderNavigationLink)[]
}

export type IHeaderNavigationElement = IHeaderNavigationLink | IHeaderNavigationMenu

export interface IHeaderNavigationLink {
  title: string
  url: string
}

export interface IHeaderNavigationLinkBadge {
  text: string
  textColor?: string
  color?: string
}

export interface IHeaderNavigationMenu {
  title: string
  content: (
    | IHeaderNavigationMenuDescription
    | IHeaderNavigationMenuSection
    | IHeaderNavigationSolutions
  )[]
  sidebar: (IHeaderNavigationSpotlight | IHeaderNavigationMenuSection)[]
}

export interface IHeaderNavigationMenuDescription {
  contentType?: string
  title?: string
  subhead?: string
  cta?: ICTALinkMVP
}

export interface IHeaderNavigationMenuItem {
  contentType?: 'flex' | 'grid'
  theme?: 'light' | 'dark' | 'resolution-bot'
  title: string
  subtitle?: string
  subhead?: React.ReactNode
  url: string
  badge?: IHeaderNavigationLinkBadge
  icon?: any
  titleIcon?: Icon
  newWindow?: boolean
  cta?: ICTALinkMVP
}

export interface IHeaderNavigationMenuSection {
  contentType?: 'flex' | 'grid'
  theme?: 'light' | 'dark' | 'resolution-bot'
  title?: string
  items: IHeaderNavigationMenuItem[]
  itemsSecondColumn?: IHeaderNavigationMenuItem[]
}

export interface IHeaderNavigationMobile {
  contentType?: 'flex' | 'grid'
  theme?: 'light' | 'dark' | 'resolution-bot'
  navigationElements: (IHeaderNavigationMenu | IHeaderNavigationLink)[]
  cta?:
    | IContentfulCtaButton
    | IContentfulCtaLink
    | IContentfulSignupCta
    | IContentfulVariationContainer
  secondCta?:
    | IContentfulCtaButton
    | IContentfulCtaLink
    | IContentfulSignupCta
    | IContentfulVariationContainer
  signInLink?: IHeaderNavigationSignInLink
  topContentHeight?: number | null
  isMobileMenuActive?: boolean
}

interface IMuteableComponent {
  isAnyComponentActive?: boolean
  setIsActive?: (isActive: boolean) => void
}

export interface IHeaderNavigationSignInLink extends IMuteableComponent {
  contentType?: 'flex' | 'grid'
  title: string
  url: string
  additionalContent?: string
}

export interface IHeaderNavigationSimplified {
  navigationElements: IHeaderNavigationElement[]
}

export interface IHeaderNavigationSolution {
  contentType?: 'flex' | 'grid'
  eyebrow?: string
  title: string
  subhead: string
  cta: ICTALinkMVP
  secondCta?: ICTALinkMVP
  additionalTitle?: string
  additionalSubhead?: string
  additionalCTA?: ICTALinkMVP
}

export interface IHeaderNavigationSolutions {
  contentType?: 'flex' | 'grid'
  title?: string
  solutions: IHeaderNavigationSolution[]
}

export interface IHeaderNavigationSpotlight {
  contentType?: 'flex' | 'grid'
  theme?: 'light' | 'dark' | 'resolution-bot'
  title: string
  description: string
  media: IImage
  url: string
  linkText: string
}

export interface IProps {
  contentType?: 'flex' | 'grid'
  theme?: 'light' | 'dark' | 'resolution-bot'
  isTheLogoClickable: boolean
  navigationElements: (IHeaderNavigationMenu | IHeaderNavigationLink)[]
  cta?:
    | IContentfulCtaButton
    | IContentfulCtaLink
    | IContentfulSignupCta
    | IContentfulVariationContainer
  secondCta?:
    | IContentfulCtaButton
    | IContentfulCtaLink
    | IContentfulSignupCta
    | IContentfulVariationContainer
  mutinyCtaContainer?: IMutinyCtasContainer
  signInLink?: IHeaderNavigationSignInLink
  enableUtilityNav?: boolean
  subNavigation?: ISubNav
}
